import React, { useContext, useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Button, Alert, Text, Box, Stack, Drawer, Paper, useMantineTheme, Loader, Center } from "@mantine/core";
import {
  createCaptureSdk,
  createCaptureUiWithInstance,
} from "@microblink/capture";
import "@microblink/capture/style.css";
import { uploadImageToS3 } from "../../../../helpers";
import { DataContext } from "../../../../context/DataContext";
import { IconExclamationCircle, IconId, IconCards, IconEPassport, IconChevronRight, IconHandClick } from "@tabler/icons-react";
import { isMobile, isFirefox } from "react-device-detect";
import FirefoxCameraWarning from "../../../../components/FirefoxCameraWarning";
import useLocale from "../../../../hooks/useLocale";
import { trackEvent } from "../../../../utils/telemetry";
import { useDeclarePortraitOnly } from "../../../../hooks/useOrientation";

export default function Microblink({ onComplete, onStart, onUpload, qrScanned, onFallbackRequested }) {
  // Not laid out for landscape: keeps the rotate prompt up here even
  // under a landscape preview flag (see OrientationWarning).
  useDeclarePortraitOnly();
  const { settings, text, data } = useContext(DataContext);
  const locale = useLocale("document-capture");
  const [sdk, setSdk] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFirefoxPrompt, setShowFirefoxPrompt] = useState(
    isMobile && isFirefox
  );
  const [showCaptureOverlay, setShowCaptureOverlay] = useState(false);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const captureStateUnsubscribeRef = useRef(null);

  // Manual fallback: show button after 20s of active capture
  const [showFallbackButton, setShowFallbackButton] = useState(false);
  const fallbackTimerRef = useRef(null);

  const clearFallbackTimer = () => {
    clearTimeout(fallbackTimerRef.current);
    fallbackTimerRef.current = null;
    setShowFallbackButton(false);
  };

  const startFallbackTimer = () => {
    clearFallbackTimer();
    fallbackTimerRef.current = setTimeout(() => {
      setShowFallbackButton(true);
    }, 10000);
  };

  // Telemetry event dedup refs
  const captureStartedFiredRef = useRef(false);
  const captureCompletedFiredRef = useRef(false);
  const captureFailedFiredRef = useRef(false);

  const theme = useMantineTheme();
  const language = (
    data?.language ||
    settings?.language ||
    settings?.text?.language ||
    text?.language ||
    ""
  ).toLowerCase();
  const isJapaneseLanguage = language === "ja";

  // Get current route settings for offer_one_sided option
  const getCurrentRouteSettings = () => {
    const journey = settings?.journey;
    if (!journey?.route) return null;
    // Find route that matches microblink path
    const currentRoute = journey.route.find(route =>
      route.path?.includes("microblink") || route.path?.includes("mitek")
    );
    return currentRoute?.settings || null;
  };

  const routeSettings = getCurrentRouteSettings();
  const hasDocumentOptions = Array.isArray(routeSettings?.document_options) && routeSettings.document_options.length > 0;
  const offerOneSided = routeSettings?.offer_one_sided === true || hasDocumentOptions;

  // Get localized button labels
  const getLocalizedLabel = (labelObj, fallback) => {
    if (!labelObj) return fallback;
    // Try exact match first, then fallback to 'en'
    return labelObj[language] || labelObj.en || fallback;
  };

  const oneSidedSectionTitle = getLocalizedLabel(
    routeSettings?.one_sided_section_title,
    language === "ja" ? "身分証明書の種類を選択してください。" : "What type of ID will you use?"
  );
  const orLabel = getLocalizedLabel(
    routeSettings?.or_label,
    language === "ja" ? "または" : "or"
  );

  // Icon map for dynamic document options
  const iconMap = {
    id: IconId,
    cards: IconCards,
    passport: IconEPassport,
  };

  // Build document options array — supports new array format or falls back to legacy fields
  const documentOptions = (() => {
    if (Array.isArray(routeSettings?.document_options) && routeSettings.document_options.length > 0) {
      return routeSettings.document_options.map((opt) => ({
        mode: opt.mode,
        label: getLocalizedLabel(opt.label, opt.mode === "one_sided" ? "Use single-sided ID" : "Use two-sided ID"),
        Icon: iconMap[opt.icon] || IconId,
      }));
    }
    // Legacy fallback: build array from old one_sided_button_label / two_sided_button_label fields
    return [
      {
        mode: "one_sided",
        label: getLocalizedLabel(routeSettings?.one_sided_button_label, "Use single-sided ID"),
        Icon: IconId,
      },
      {
        mode: "two_sided",
        label: getLocalizedLabel(routeSettings?.two_sided_button_label, "Use two-sided ID"),
        Icon: IconCards,
      },
    ];
  })();

  async function processAndUpload(data, width, height, suffix) {
    const encodeStart = performance.now();
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.putImageData(new ImageData(data, width, height), 0, 0);
    // JPEG quality 0.87 (was 1.0): the Aug-Sep 2026 A/B cut upload size
    // 73% and median upload time 51% with PASS rate unchanged-to-better.
    const quality = 0.87;
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    );
    // compress_ms covers canvas draw + JPEG encode so the A/B shows the
    // encode-time trade-off next to the upload-time win; upload_ms makes
    // duration a one-row query instead of a started/completed pair-join.
    const compressMs = Math.round(performance.now() - encodeStart);
    trackEvent("id_doc_upload_started", { provider: "microblink", image: suffix, bytes: blob?.size, quality, compress_ms: compressMs });
    const uploadStart = performance.now();
    try {
      await uploadImageToS3(`${settings.request_id}_dl_${suffix}.jpeg`, blob, settings);
      trackEvent("id_doc_upload_completed", { provider: "microblink", image: suffix, quality, upload_ms: Math.round(performance.now() - uploadStart) });
    } catch (err) {
      trackEvent("id_doc_upload_failed", { provider: "microblink", image: suffix, error: err?.message || String(err), upload_ms: Math.round(performance.now() - uploadStart) });
      throw err;
    }
  }

  // Fire-and-forget stats logging - failures should not block the user flow
  function sendResultToApi(requestId, documentGroup, cameraInfo = null) {
    const payload = {
      request_id: requestId,
      document_group: documentGroup,
    };

    // Add camera info if provided (send raw data without filtering)
    if (cameraInfo) {
      payload.camera_info = {
        name: cameraInfo.name,
        deviceInfo: cameraInfo.deviceInfo,
        settings: cameraInfo.settings,
      };
    }

    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/microblink/stats`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    )
      .then((response) => response.json())
      .then((result) => {
        if (result.result !== "success") {
          console.error("Failed to send result to API:", result);
        }
      })
      .catch((error) => {
        // Log error but don't block - stats are non-critical
        console.error("Error while sending stats to API:", error);
      });
  }

  const captureComponentRef = useRef(null);
  // guard against multiple simultaneous capture sessions
  const isCapturingRef = useRef(false);

  // Fallback license key in case primary key has issues
  const FALLBACK_LICENSE_KEY = "sRwCAAsqLmRjYW1zLmFwcAZsZXlKRGNtVmhkR1ZrVDI0aU9qRTNOalF3T0RRME1qSXhNak1zSWtOeVpXRjBaV1JHYjNJaU9pSXlZemhtTnpCak15MDBPVEJtTFdVd01UUXROR0ppTlMweU9XVmpNbVptTlRkaE5UZ2lmUT093OXef1o3RWwzUKnc1+HaeRjVgR1Wv/gfkuOAeSyuFxwHUgUwewwXcqV33+cVXeNIq/MHGyFxsEz4EN0eG9oBXLgeJvuHnygJOWIq7MjWr828ftm400to2umKIlPl";

  useEffect(() => {
    // Preload the SDK (skip if offerOneSided - we'll load after user selection)
    async function preloadSdk() {
      if (showFirefoxPrompt) return;
      if (offerOneSided) return; // Don't preload - wait for user selection
      setIsLoading(true);

      // Helper function to initialize SDK with a given key
      async function initializeSdkWithKey(licenseKey, keyName) {
        console.log(`Attempting to initialize Microblink SDK with ${keyName} (first 8 chars):`, licenseKey?.substring(0, 8));

        const loadedSdk = await createCaptureSdk({
          licenseKey: licenseKey,
          resourceUrl: `${window.location.protocol}//${window.location.hostname}:${window.location.port}`,
        });

        // Disable blur detection for all desktop devices and specified sites
        if(settings.site_name === 'GAVS-PROD-TEST' ||
           settings.site_name === "GAVS-PROD-RO" ||
           !isMobile){
          await loadedSdk.updateAnalyzerSettings({
            blurPolicy: "disabled", // Disables blur detection
            glarePolicy: "disabled" // Also disable glare detection
          });
        }

        // For Veratad sites, optimize for quality with enhanced settings
        if(settings.site_name && settings.site_name.toLowerCase().includes('veratad')){
          await loadedSdk.updateAnalyzerSettings({
            captureStrategy: "optimize-for-quality",
            adjustMinimumDocumentDpi: true,
            minimumDocumentDpi: 400,
            keepDpiOnTransformedDocumentImage: true,
            lightingThresholds: {
              // Allow darker lighting; frames below this are too dark (0-1 scale)
              tooDarkThreshold: 0.1,
            }
          });
        }

        // For KID environments, allow darker ambient lighting before capture
        if (
          settings.site_name &&
          ["kid-qa", "kid-pre-prod"].includes(
            settings.site_name.toLowerCase()
          )
        ) {
          await loadedSdk.updateAnalyzerSettings({
            lightingThresholds: {
              // Allow darker ambient lighting; frames below this are considered too dark
              tooDarkThreshold: 0.1,
            },
          });
        }

        // For front-only capture, skip the back side
        // Skip this if offerOneSided is true - we'll set it when user makes selection
        if (settings.journey?.settings?.frontIdOnly && !offerOneSided) {
          await loadedSdk.updateAnalyzerSettings({
            captureSingleSide: true,
          });
        }

        return loadedSdk;
      }

      try {
        // Try with primary key first
        const mbKey = process.env.REACT_APP_MB_KEY;
        const loadedSdk = await initializeSdkWithKey(mbKey, "primary key");
        console.log("Microblink SDK initialized successfully with primary key");
        setSdk(loadedSdk);
      } catch (primaryError) {
        console.warn("Primary license key failed:", primaryError.message);

        // Check if it's a license-related error and try fallback
        const isLicenseError = primaryError.message?.toLowerCase().includes('license') ||
                               primaryError.message?.toLowerCase().includes('expired') ||
                               primaryError.message?.toLowerCase().includes('invalid');

        if (isLicenseError) {
          console.log("Attempting fallback license key...");
          try {
            const loadedSdk = await initializeSdkWithKey(FALLBACK_LICENSE_KEY, "fallback key");
            console.log("Microblink SDK initialized successfully with fallback key");
            setSdk(loadedSdk);
          } catch (fallbackError) {
            console.error("Fallback license key also failed:", fallbackError.message);
            sendResultToApi(settings.request_id, fallbackError.message);
            setErrorMessage("Failed to initialize the SDK: " + fallbackError.message);
          }
        } else {
          // Non-license error, don't retry
          sendResultToApi(settings.request_id, primaryError.message);
          setErrorMessage("Failed to initialize the SDK: " + primaryError.message);
        }
      }
      setIsLoading(false);
    }

    preloadSdk();

    // Cleanup on component unmount
    return () => {
      clearFallbackTimer();
      captureStateUnsubscribeRef.current?.();
      captureComponentRef.current?.dismount();
      setShowCaptureOverlay(false);
    };
  }, [showFirefoxPrompt]);

  // Start capture with a provided SDK instance (used when SDK is loaded on-demand)
  async function startCaptureWithSdk(sdkInstance, captureMode) {
    if (isCapturingRef.current) return;
    isCapturingRef.current = true;
    onStart();

    if (!captureStartedFiredRef.current) {
      captureStartedFiredRef.current = true;
      trackEvent("id_doc_capture_started", {
        provider: "microblink",
      });
    }

    if (isJapaneseLanguage) {
      setShowCaptureOverlay(true);
    }

    await sendResultToApi(settings.request_id, `SDK loaded - capture mode: ${captureMode}`);

    try {
      const component = await createCaptureUiWithInstance(sdkInstance, {
        target: undefined,
        showTutorial: false,
        showErrorDialog: false,
        destroyInstanceOnDismount: true,
        localization: {
          ...text.captureMessages,
          ...text.tutorialMessages,
          ...text.cameraMessages,
          ...text.sharedMessages,
        },
      });

      const originalDismount = component.dismount.bind(component);
      component.dismount = () => {
        captureStateUnsubscribeRef.current?.();
        setShowCaptureOverlay(false);
        setIsLoading(false);
        isCapturingRef.current = false;
        return originalDismount();
      };

      captureStateUnsubscribeRef.current?.();
      captureStateUnsubscribeRef.current = component.captureSdk?.subscribe(
        (s) => s.isPlaying || s.isCapturing,
        (isActive) => {
          if (isActive) {
            // The SDK owns getUserMedia — isPlaying flipping true is the
            // closest observable "camera streaming" signal; keeps the
            // microblink funnel aligned with veratad's id_doc_camera_ready.
            trackEvent("id_doc_camera_ready", { provider: "microblink" }, { once: true });
            startFallbackTimer();
            if (isJapaneseLanguage) setShowCaptureOverlay(true);
          } else {
            clearFallbackTimer();
            setShowCaptureOverlay(false);
            setIsLoading(false);
            isCapturingRef.current = false;
          }
        }
      );

      component.captureSdk?.subscribe(
        (s) => s.errorState,
        async (x) => {
          if (x?.message !== undefined) {
            clearFallbackTimer();
            setErrorMessage(x?.message);
            console.error("Error from the SDK: ", x?.message);

            if (!captureFailedFiredRef.current) {
              captureFailedFiredRef.current = true;
              trackEvent("id_doc_capture_failed", {
                provider: "microblink",
                error_type: x?.message || "sdk_error",
              });
            }

            try {
              await sendResultToApi(settings.request_id, x?.message);
            } catch (error) {
              console.error("Error sending result to API:", error);
            }
            component.dismount();
            setIsLoading(false);
            isCapturingRef.current = false;
            setShowCaptureOverlay(false);
          }
        }
      );

      component.captureSdk?.setCallbacks({
        onCaptureResult: async (result) => {
          clearFallbackTimer();
          // Frames in hand — microblink's per-side id_doc_captured,
          // matching the veratad flow's stage shape.
          trackEvent("id_doc_captured", {
            provider: "microblink",
            side: "front",
            doc_type: result.documentGroup,
          });
          if (result.secondCapture?.imageResult) {
            trackEvent("id_doc_captured", {
              provider: "microblink",
              side: "back",
              doc_type: result.documentGroup,
            });
          }
          const selectedCamera = component.captureSdk?.getState().selectedCamera;
          await sendResultToApi(settings.request_id, result.documentGroup, selectedCamera);

          if (settings.journey.settings?.checkMbDocumentGroup && result.documentGroup === "unknown") {
            component.dismount();
            setErrorMessage("The document type is unknown. Please try again.");
            setIsLoading(false);
            isCapturingRef.current = false;
            setShowCaptureOverlay(false);
            return;
          }

          component.dismount();
          onUpload();

          // Build array of upload promises for parallel execution
          const uploadPromises = [
            processAndUpload(
              result.firstCapture.imageResult.data,
              result.firstCapture.imageResult.width,
              result.firstCapture.imageResult.height,
              "front"
            )
          ];

          if (result.secondCapture?.imageResult) {
            uploadPromises.push(
              processAndUpload(
                result.secondCapture.imageResult.data,
                result.secondCapture.imageResult.width,
                result.secondCapture.imageResult.height,
                "back"
              )
            );
          }

          // Upload front and back images in parallel
          await Promise.all(uploadPromises);

          if (!captureCompletedFiredRef.current) {
            captureCompletedFiredRef.current = true;
            trackEvent("id_doc_capture_completed", {
              provider: "microblink",
            });
          }

          onComplete();
          setIsLoading(false);
          setShowCaptureOverlay(false);
          isCapturingRef.current = false;
        },
      });

      captureComponentRef.current = component;
    } catch (error) {
      await sendResultToApi(settings.request_id, error.message);

      if (!captureFailedFiredRef.current) {
        captureFailedFiredRef.current = true;
        trackEvent("id_doc_capture_failed", {
          provider: "microblink",
          error_type: error?.message || "sdk_start_error",
        });
      }

      setErrorMessage("Failed to start the capture process: " + error.message);
      setIsLoading(false);
      setShowCaptureOverlay(false);
      isCapturingRef.current = false;
    }
  }

  async function startCapture(selectedCaptureMode = null) {
    // prevent starting the capture process if one is already running
    if (isCapturingRef.current) {
      return;
    }
    isCapturingRef.current = true;
    setIsLoading(true);
    setDrawerOpened(false);
    onStart();

    if (!captureStartedFiredRef.current) {
      captureStartedFiredRef.current = true;
      trackEvent("id_doc_capture_started", {
        provider: "microblink",
      });
    }
    if (!sdk) {
      sendResultToApi(
        settings.request_id,
        "SDK not initialized, please reload the page."
      );
      setErrorMessage("SDK not initialized, please reload the page.");
      setIsLoading(false);
      isCapturingRef.current = false;
      return;
    }
    if (isJapaneseLanguage) {
      setShowCaptureOverlay(true);
    }

    // Apply capture mode setting if offer_one_sided is enabled and mode was selected
    if (offerOneSided && selectedCaptureMode) {
      const shouldCaptureSingleSide = selectedCaptureMode === "one_sided";
      await sdk.updateAnalyzerSettings({
        captureSingleSide: shouldCaptureSingleSide,
      });
      await sendResultToApi(settings.request_id, `SDK loaded - capture mode: ${selectedCaptureMode}`);
    } else {
      await sendResultToApi(settings.request_id, "SDK loaded");
    }

    try {
      const component = await createCaptureUiWithInstance(sdk, {
        target: undefined,
        showTutorial: false,
        showErrorDialog: false,
        destroyInstanceOnDismount: true,
        localization: {
          ...text.captureMessages,
          ...text.tutorialMessages,
          ...text.cameraMessages,
          ...text.sharedMessages,
        },
      });

      const originalDismount = component.dismount.bind(component);
      component.dismount = () => {
        captureStateUnsubscribeRef.current?.();
        setShowCaptureOverlay(false);
        setIsLoading(false);
        isCapturingRef.current = false;
        return originalDismount();
      };

      captureStateUnsubscribeRef.current?.();
      captureStateUnsubscribeRef.current = component.captureSdk?.subscribe(
        (s) => s.isPlaying || s.isCapturing,
        (isActive) => {
          if (isActive) {
            // The SDK owns getUserMedia — isPlaying flipping true is the
            // closest observable "camera streaming" signal; keeps the
            // microblink funnel aligned with veratad's id_doc_camera_ready.
            trackEvent("id_doc_camera_ready", { provider: "microblink" }, { once: true });
            startFallbackTimer();
            if (isJapaneseLanguage) setShowCaptureOverlay(true);
          } else {
            clearFallbackTimer();
            setShowCaptureOverlay(false);
            // Ensure the intro button becomes clickable again when SDK is closed
            setIsLoading(false);
            isCapturingRef.current = false;
          }
        }
      );

      component.captureSdk?.subscribe(
        (s) => s.errorState,
        async (x) => {
          if (x?.message !== undefined) {
            clearFallbackTimer();
            setErrorMessage(x?.message);
            console.error("Error from the SDK: ", x?.message);

            if (!captureFailedFiredRef.current) {
              captureFailedFiredRef.current = true;
              trackEvent("id_doc_capture_failed", {
                provider: "microblink",
                error_type: x?.message || "sdk_error",
              });
            }

            try {
              sendResultToApi(settings.request_id, x?.message);
            } catch (error) {
              console.error("Error sending result to API:", error);
            }

            component.dismount();
            setIsLoading(false);
            isCapturingRef.current = false;
            setShowCaptureOverlay(false);
          }
        }
      );

      component.captureSdk?.setCallbacks({
        onCaptureResult: async (result) => {
          clearFallbackTimer();
          // Frames in hand — microblink's per-side id_doc_captured,
          // matching the veratad flow's stage shape.
          trackEvent("id_doc_captured", {
            provider: "microblink",
            side: "front",
            doc_type: result.documentGroup,
          });
          if (result.secondCapture?.imageResult) {
            trackEvent("id_doc_captured", {
              provider: "microblink",
              side: "back",
              doc_type: result.documentGroup,
            });
          }
          // Get camera info from SDK state
          const selectedCamera = component.captureSdk?.getState().selectedCamera;

          sendResultToApi(settings.request_id, result.documentGroup, selectedCamera);

          if (
            settings.journey.settings?.checkMbDocumentGroup &&
            result.documentGroup === "unknown"
          ) {
            component.dismount();
            setErrorMessage("The document type is unknown. Please try again.");
            setIsLoading(false);
            isCapturingRef.current = false;
            setShowCaptureOverlay(false);
            return;
          }

          component.dismount();
          onUpload();

          // Build array of upload promises for parallel execution
          const uploadPromises = [
            processAndUpload(
              result.firstCapture.imageResult.data,
              result.firstCapture.imageResult.width,
              result.firstCapture.imageResult.height,
              "front"
            )
          ];

          if (result.secondCapture?.imageResult) {
            uploadPromises.push(
              processAndUpload(
                result.secondCapture.imageResult.data,
                result.secondCapture.imageResult.width,
                result.secondCapture.imageResult.height,
                "back"
              )
            );
          }

          // Upload front and back images in parallel
          await Promise.all(uploadPromises);

          if (!captureCompletedFiredRef.current) {
            captureCompletedFiredRef.current = true;
            trackEvent("id_doc_capture_completed", {
              provider: "microblink",
            });
          }

          onComplete();
          setIsLoading(false);
          setShowCaptureOverlay(false);
          isCapturingRef.current = false;
        },
      });

      captureComponentRef.current = component;
    } catch (error) {
      sendResultToApi(settings.request_id, error.message);

      if (!captureFailedFiredRef.current) {
        captureFailedFiredRef.current = true;
        trackEvent("id_doc_capture_failed", {
          provider: "microblink",
          error_type: error?.message || "sdk_start_error",
        });
      }

      setErrorMessage("Failed to start the capture process: " + error.message);
      setIsLoading(false);
      setShowCaptureOverlay(false);
      isCapturingRef.current = false;
    }
  }

  // Handle button click - show drawer if offerOneSided, otherwise start capture
  const handleButtonClick = () => {
    if (offerOneSided) {
      setDrawerOpened(true);
    } else {
      startCapture();
    }
  };

  // Handle document type selection from drawer - load SDK then start capture
  const handleDocumentTypeSelection = async (mode) => {
    setDrawerLoading(true);

    try {
      const mbKey = process.env.REACT_APP_MB_KEY;
      console.log('Microblink SDK key (first 8 chars):', mbKey?.substring(0, 8));

      const loadedSdk = await createCaptureSdk({
        licenseKey: mbKey,
        resourceUrl: `${window.location.protocol}//${window.location.hostname}:${window.location.port}`,
      });

      // Apply standard settings
      if (settings.site_name === 'GAVS-PROD-TEST' ||
          settings.site_name === "GAVS-PROD-RO" ||
          !isMobile) {
        await loadedSdk.updateAnalyzerSettings({
          blurPolicy: "disabled",
          glarePolicy: "disabled"
        });
      }

      if (settings.site_name && settings.site_name.toLowerCase().includes('veratad')) {
        await loadedSdk.updateAnalyzerSettings({
          captureStrategy: "optimize-for-quality",
          adjustMinimumDocumentDpi: true,
          minimumDocumentDpi: 400,
          keepDpiOnTransformedDocumentImage: true,
          lightingThresholds: {
            tooDarkThreshold: 0.1,
          }
        });
      }

      if (settings.site_name &&
          ["kid-qa", "kid-pre-prod"].includes(settings.site_name.toLowerCase())) {
        await loadedSdk.updateAnalyzerSettings({
          lightingThresholds: {
            tooDarkThreshold: 0.1,
          },
        });
      }

      // Apply single-side setting based on user selection
      const shouldCaptureSingleSide = mode === "one_sided";
      await loadedSdk.updateAnalyzerSettings({
        captureSingleSide: shouldCaptureSingleSide,
      });

      setSdk(loadedSdk);

      // Now start capture with the loaded SDK
      setDrawerOpened(false);
      setDrawerLoading(false);
      setIsLoading(true);
      setTimeout(() => {
        startCaptureWithSdk(loadedSdk, mode);
      }, 100);

    } catch (error) {
      await sendResultToApi(settings.request_id, error.message);
      setErrorMessage("Failed to initialize the SDK: " + error.message);
      setDrawerOpened(false);
      setDrawerLoading(false);
      setIsLoading(false);
    }
  };

  if (showFirefoxPrompt) {
    return (
      <FirefoxCameraWarning onContinue={() => setShowFirefoxPrompt(false)} />
    );
  }

  return (
    <>
      {errorMessage && (
        <Alert
          icon={<IconExclamationCircle />}
          color="red"
          title={errorMessage}
        >
          {errorMessage !==
            "The document type is unknown. Please try again." && (
            <Text>{text.dcams.errors.permissions}</Text>
          )}
        </Alert>
      )}

      <Button
        size="lg"
        fullWidth
        onClick={handleButtonClick}
        disabled={offerOneSided ? (isLoading || qrScanned) : (!sdk || isLoading || qrScanned)}
        loading={offerOneSided ? isLoading : (!sdk || isLoading)}
        loaderPosition="left"
      >
        {text.dcams.button}
      </Button>

      {/* Document type selection drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        position="bottom"
        size="auto"
        withCloseButton={false}
        styles={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          },
          inner: {
            padding: "0 8px 8px 8px",
          },
          content: {
            borderRadius: "12px !important",
            border: "1px solid #d0d0d0",
            boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
          },
          body: {
            padding: "20px 20px 28px 20px",
          },
        }}
      >
        <Stack spacing="md">
          {drawerLoading ? (
            <Center py="xl">
              <Stack align="center" spacing="md">
                <Loader size="md" />
                <Text size="sm" c="dimmed">
                  {language === "ja" ? "準備中..." : "Getting ready..."}
                </Text>
              </Stack>
            </Center>
          ) : (
            <>
              <Text fw={600} size="lg" ta="center">
                {oneSidedSectionTitle}
              </Text>

              <Stack spacing="sm">
                {documentOptions.map((option, index) => (
                  <React.Fragment key={option.mode + index}>
                    {index > 0 && documentOptions[index - 1].mode !== option.mode && (
                      <Text size="sm" c="dimmed" ta="center" py={4}>
                        {orLabel}
                      </Text>
                    )}
                    <Paper
                      onClick={() => handleDocumentTypeSelection(option.mode)}
                      p="md"
                      withBorder
                      sx={{
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          borderColor: theme.colors.blue[5],
                          backgroundColor:
                            theme.colorScheme === "dark"
                              ? theme.colors.dark[5]
                              : theme.colors.gray[0],
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <option.Icon size={28} style={{ marginRight: 12, flexShrink: 0, color: theme.colors.blue[6] }} />
                          <Text fw={500}>{option.label}</Text>
                        </Box>
                        <IconChevronRight size={20} style={{ flexShrink: 0, color: theme.colors.gray[5] }} />
                      </Box>
                    </Paper>
                  </React.Fragment>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </Drawer>
      {showCaptureOverlay && isJapaneseLanguage && (
        <Box
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 16,
            display: "flex",
            justifyContent: "center",
            zIndex: 9999,
            pointerEvents: "none",
          }}
        >
          <Box
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.85)",
              color: "white",
              padding: "12px 16px",
              borderRadius: 8,
              maxWidth: 520,
              boxShadow: "0 6px 18px rgba(0, 0, 0, 0.35)",
            }}
          >
            <Text align="center" fz="sm">
              マイナンバーカード：表面のみスキャンしてください。裏面は絶対にスキャンしないでください。
            </Text>
          </Box>
        </Box>
      )}
      {showFallbackButton && ReactDOM.createPortal(
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 90,
            display: "flex",
            justifyContent: "center",
            zIndex: 999999,
            animation: "fallbackFadeIn 0.4s ease-in-out",
            pointerEvents: "auto",
          }}
        >
          <button
            onClick={() => {
              clearFallbackTimer();
              trackEvent("id_doc_capture_fallback_to_manual", {
                provider: "microblink_to_veratad",
              });
              captureComponentRef.current?.dismount();
              onFallbackRequested?.();
            }}
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              color: "white",
              borderRadius: 28,
              padding: "14px 28px",
              fontSize: 16,
              fontWeight: 600,
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255, 255, 255, 0.25)",
              cursor: "pointer",
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              gap: 10,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.4)",
            }}
          >
            <IconHandClick size={22} stroke={2} />
            {locale?.errors?.manualFallback || "Having trouble? Tap for manual capture"}
          </button>
          <style>{`
            @keyframes fallbackFadeIn {
              from { opacity: 0; transform: translateY(10px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>,
        document.body
      )}
    </>
  );
}
